import { Phone, Globe, ArrowRight, BadgeCheck, CalendarCheck } from "lucide-react";
import {
  HEADSHOT_IMAGE,
  LO_NAME,
  LO_NMLS,
  PHONE_NUMBER,
  PHONE_TEL,
  LO_SITE,
  LO_SITE_LABEL,
  APPLY_URL,
  BOOK_URL,
} from "@/lib/site";

export default function LoanOfficer() {
  return (
    <section
      id="officer"
      data-testid="loan-officer-section"
      className="py-20 md:py-28 lg:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Image side */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-[#0A2F6B]">
            <img
              src={HEADSHOT_IMAGE}
              alt={`${LO_NAME} — Loan Officer at PRMG`}
              className="w-full h-full object-cover"
              data-testid="loan-officer-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A2F6B] via-[#0A2F6B]/60 to-transparent p-6">
              <p className="text-white/80 text-xs uppercase tracking-[0.18em] mb-1">Your Loan Officer</p>
              <p className="font-display font-bold text-white text-2xl md:text-3xl leading-tight">{LO_NAME}</p>
              <p className="text-[#F26A21] text-sm font-semibold mt-1">NMLS# {LO_NMLS}</p>
            </div>
          </div>
          {/* Accent block */}
          <div className="hidden md:block absolute -bottom-6 -right-6 w-32 h-32 bg-[#F26A21] -z-10 rounded-md" aria-hidden="true" />
        </div>

        {/* Content side */}
        <div className="lg:col-span-7">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#F26A21] mb-3">
            Meet your loan officer
          </p>
          <h2
            data-testid="officer-heading"
            className="font-display font-bold text-[#0A2F6B] text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight"
          >
            Personal guidance for one of life&rsquo;s biggest decisions.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl">
            {LO_NAME} works one-on-one with homeowners to help them understand whether a HELOC
            is the right fit. From the first conversation through funding, you&rsquo;ll have a clear
            partner who answers your questions, sets realistic expectations, and respects your time.
          </p>

          <ul className="mt-7 space-y-3 max-w-xl">
            {[
              "Clear, no-pressure conversations &mdash; ask anything.",
              "Help understanding rates, draw periods, and how a HELOC fits your goals.",
              "Backed by PRMG (NMLS #75243) &mdash; a nationally-licensed mortgage lender.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <BadgeCheck size={20} className="text-[#F26A21] shrink-0 mt-0.5" strokeWidth={2.5} />
                <span
                  className="text-[#0F172A] text-sm md:text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: line }}
                />
              </li>
            ))}
          </ul>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 max-w-xl">
            <a
              href={PHONE_TEL}
              data-testid="officer-phone-link"
              className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] hover:border-[#0A2F6B] rounded-md p-4 transition-all duration-200 hover:shadow-md group"
            >
              <div className="w-10 h-10 rounded-md bg-[#0A2F6B] flex items-center justify-center group-hover:bg-[#F26A21] transition-colors">
                <Phone size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-semibold">Phone</p>
                <p className="font-display font-semibold text-[#0A2F6B] text-base tabular-nums">{PHONE_NUMBER}</p>
              </div>
            </a>
            <a
              href={LO_SITE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="officer-site-link"
              className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] hover:border-[#0A2F6B] rounded-md p-4 transition-all duration-200 hover:shadow-md group"
            >
              <div className="w-10 h-10 rounded-md bg-[#0A2F6B] flex items-center justify-center group-hover:bg-[#F26A21] transition-colors">
                <Globe size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-semibold">Online</p>
                <p className="font-display font-semibold text-[#0A2F6B] text-sm">{LO_SITE_LABEL}</p>
              </div>
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="officer-apply-button"
              className="inline-flex items-center gap-2 bg-[#F26A21] hover:bg-[#D95A1A] text-white font-semibold text-base px-6 py-3.5 rounded-md shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              Start your application
              <ArrowRight size={18} strokeWidth={2.5} />
            </a>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="officer-book-button"
              className="inline-flex items-center gap-2 bg-white hover:bg-[#F1F5F9] text-[#0A2F6B] border border-[#0A2F6B]/15 hover:border-[#0A2F6B]/40 font-semibold text-base px-6 py-3.5 rounded-md shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <CalendarCheck size={18} strokeWidth={2.5} />
              Book a Meeting
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
